<template>
  <div
    :title="selectedOrganisationName"
    class="border flex items-center mx-4 px-4 py-3 mb-8 relative rounded-xl shrink-0 space-x-4 text-white relative"
    :class="[
      canSelectOrganisation
        ? 'cursor-pointer hover:bg-cw-700'
        : 'cursor-default',
    ]"
    @click="toggleShowOrganisations"
  >
    <!-- Logo -->
    <div
      class="rounded-full flex items-center justify-center border-2 w-6 h-6 relative flex-shrink-0 contributor-badge-cw"
    >
      <template v-if="!isLoading">
        <div
          v-if="selectedOrganisation?.logo"
          class="bg-no-repeat bg-cover bg-center w-full h-full rounded-full"
          :style="{ backgroundImage: 'url(' + selectedOrganisation.logo + ')' }"
        />
        <div
          v-else
          class="flex justify-center items-center w-full h-full rounded-full text-white"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            fill="currentColor"
            class="h-7 w-7"
          >
            <path
              d="M10 9a3 3 0 1 0 0-6 3 3 0 0 0 0 6ZM6 8a2 2 0 1 1-4 0 2 2 0 0 1 4 0ZM1.49 15.326a.78.78 0 0 1-.358-.442 3 3 0 0 1 4.308-3.516 6.484 6.484 0 0 0-1.905 3.959c-.023.222-.014.442.025.654a4.97 4.97 0 0 1-2.07-.655ZM16.44 15.98a4.97 4.97 0 0 0 2.07-.654.78.78 0 0 0 .357-.442 3 3 0 0 0-4.308-3.517 6.484 6.484 0 0 1 1.907 3.96 2.32 2.32 0 0 1-.026.654ZM18 8a2 2 0 1 1-4 0 2 2 0 0 1 4 0ZM5.304 16.19a.844.844 0 0 1-.277-.71 5 5 0 0 1 9.947 0 .843.843 0 0 1-.277.71A6.975 6.975 0 0 1 10 18a6.974 6.974 0 0 1-4.696-1.81Z"
            />
          </svg>
        </div>
      </template>
    </div>
    <!-- /Logo -->

    <!-- Loading -->
    <div v-if="isLoading" class="flex justify-center items-center w-full">
      <div class="w-6 h-6">
        <lottie :options="loadingAnimationOptions" />
      </div>
    </div>
    <!-- /Loading -->

    <!-- Text -->
    <div v-else class="grid grid-cols-5 gap-2">
      <div class="flex items-center col-span-4">
        <h2
          v-if="selectedOrganisationName"
          class="font-montserrat mb-0 text-sm truncate"
        >
          {{ selectedOrganisationName }}
        </h2>
      </div>

      <div class="flex items-center justify-end col-span-1">
        <svg
          v-if="canSelectOrganisation"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 20 20"
          fill="currentColor"
          class="w-5 h-5"
        >
          <path
            fill-rule="evenodd"
            d="M5.22 8.22a.75.75 0 0 1 1.06 0L10 11.94l3.72-3.72a.75.75 0 1 1 1.06 1.06l-4.25 4.25a.75.75 0 0 1-1.06 0L5.22 9.28a.75.75 0 0 1 0-1.06Z"
            clip-rule="evenodd"
          />
        </svg>
      </div>
    </div>
    <!-- /Text -->

    <!-- Selector dropdown -->
    <div
      v-if="isSelectingOrganisation"
      class="mx-0 absolute right-0 top-[105%] bg-white rounded-md shadow-lg w-full"
    >
      <ul ref="organisationsListRef" class="max-h-44 w-full overflow-y-scroll">
        <li
          v-for="(item, index) in sortedOrganisations"
          :key="item.id"
          class="group py-2 px-4 cursor-pointer flex flex-row gap-1 items-center"
          :class="{
            'border-b border-truegray-200': index < organisations.length - 1,
          }"
        >
          <span
            class="block w-full text-xs group-hover:text-cw-alt"
            :class="
              isSelectedOrganisation(item.id) ? 'text-cw-alt' : 'text-cw-900'
            "
            @click="() => selectOrganisation(item.id)"
          >
            {{ item.title }}
          </span>
        </li>
      </ul>
    </div>
    <!-- /Selector dropdown -->
  </div>
</template>

<script>
import { useStore } from '../../../js/store/index.js'
import { useRoute } from 'vue-router/composables'
import * as loadingAnimation from '../../../shared/components/loading.json'
import { defineComponent, ref, computed } from 'vue'

export default defineComponent({
  name: 'OrganisationSelector',

  setup() {
    const store = useStore()
    const route = useRoute()
    const loadingAnimationOptions = { animationData: loadingAnimation.default }
    const isLoading = ref(false)

    const organisations = computed(
      () => store.getters['contributors/getOrganisations'],
    )

    const sortedOrganisations = computed(() => {
      return organisations.value.slice().sort((a, b) => {
        return a.title.localeCompare(b.title)
      })
    })
    const selectedOrganisation = computed(
      () => store.getters['contributors/getOrganisation'],
    )
    const selectedOrganisationId = computed(
      () => store.getters['contributors/getOrganisationId'],
    )
    const selectedOrganisationName = computed(
      () => store.getters['contributors/getOrganisationName'],
    )

    const isSelectingOrganisation = ref(false)

    const isSelectedOrganisation = (id) => id === selectedOrganisationId.value
    const canSelectOrganisation = computed(() => {
      return organisations.value?.length > 1
    })

    const toggleShowOrganisations = () => {
      if (canSelectOrganisation.value) {
        isSelectingOrganisation.value = !isSelectingOrganisation.value
      }
    }

    const selectOrganisation = async (id) => {
      if (id !== selectedOrganisationId.value) {
        isLoading.value = true
        await store.dispatch('contributors/dispatchSetOrganisation', id)
        isLoading.value = false

        const actions = route.meta?.actions
        if (actions) {
          await store.dispatch('shared/dispatchUpdateIsLoading', true)
          for (const action of actions) {
            await store.dispatch(action)
          }
          await store.dispatch('shared/dispatchUpdateIsLoading')
        }
      }
    }

    return {
      loadingAnimationOptions,
      isLoading,

      organisations,
      sortedOrganisations,
      selectedOrganisation,
      selectedOrganisationName,
      selectedOrganisationId,
      isSelectedOrganisation,
      canSelectOrganisation,
      isSelectingOrganisation,
      toggleShowOrganisations,
      selectOrganisation,
    }
  },
})
</script>
